import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { clearDraft } from "@/lib/draft";
import { api } from "@/lib/api";
import { clearAll as clearSummariesCache } from "@/lib/summariesCache";
import { getStoredDeviceId } from "@/contexts/GuestContext";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("sux1_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sux1_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("sux1_user", JSON.stringify(res.data));
      })
      .catch((err) => {
        // Only invalidate the session on an authoritative 401/403. Network
        // errors (offline, DNS down, server 5xx) must NOT log the user out —
        // otherwise a metro/airplane user would lose access to the cached
        // history the moment they reopen the app in a dead zone.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("sux1_token");
          localStorage.removeItem("sux1_user");
          setUser(null);
        }
        // Else: keep the locally-cached user (from state initializer) so the
        // protected routes stay accessible for read-only offline usage.
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sux1_token", data.token);
    localStorage.setItem("sux1_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    // Attach the current device_id so the backend can migrate the anonymous
    // summary counter onto the new user (3 anon → user starts at 3/10).
    let device_id = null;
    try {
      device_id = getStoredDeviceId();
    } catch {
      /* ignore */
    }
    const { data } = await api.post("/auth/register", { name, email, password, device_id });
    localStorage.setItem("sux1_token", data.token);
    localStorage.setItem("sux1_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("sux1_token");
    clearDraft();
    localStorage.removeItem("sux1_user");
    // Wipe cached summaries so switching account on a shared device never
    // leaks the previous user's history.
    clearSummariesCache();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
